import { lazy, Suspense, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, AUTH_PROVIDER } from '../contexts/AuthContext'
import PageMeta from './PageMeta'

const SupabaseAuthCallback = lazy(() => import('./SupabaseAuthCallback'))

// Post-sign-in router (Clerk's sign-in/up fallback redirect lands here, and /auth/callback stays a
// valid URL). Sends the user back where they were, or to /profile-setup if the profile is unfinished.
function ClerkAuthCallback() {
  const navigate = useNavigate()
  const { user, profile, loading, accountError, clerkSignedIn, authUnavailable } = useAuth()
  const [error, setError] = useState(null)

  useEffect(() => {
    if (loading) return
    if (authUnavailable || !clerkSignedIn) {
      navigate('/login', { replace: true })
      return
    }
    if (!user) {
      setError(accountError || 'Sign-in failed. Redirecting to login...')
      const timer = setTimeout(() => navigate('/login', { replace: true }), 2500)
      return () => clearTimeout(timer)
    }

    if (profile?.username && profile?.display_name) {
      const redirectUrl = sessionStorage.getItem('redirectAfterLogin')
      if (redirectUrl) {
        sessionStorage.removeItem('redirectAfterLogin')
        navigate(redirectUrl, { replace: true })
      } else {
        navigate('/', { replace: true })
      }
    } else {
      navigate('/profile-setup', {
        state: {
          user,
          email: user.email,
          name: user.user_metadata?.full_name || '',
          avatar_url: user.user_metadata?.avatar_url || '',
          ...(profile ? { existingProfile: profile } : {}),
        },
        replace: true,
      })
    }
  }, [loading, user, profile, accountError, clerkSignedIn, authUnavailable, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark">
      <PageMeta title="Signing in | Lilycove" description="Completing sign-in to Lilycove." />
      <div className="text-center">
        {error ? (
          <>
            <p className="text-red-400 mb-2">{error}</p>
            <p className="text-cream/50 text-sm">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple mx-auto mb-4"></div>
            <p className="text-cream">Completing sign in...</p>
          </>
        )}
      </div>
    </div>
  )
}

function AuthCallback() {
  if (AUTH_PROVIDER === 'supabase') {
    return (
      <Suspense fallback={null}>
        <SupabaseAuthCallback />
      </Suspense>
    )
  }
  return <ClerkAuthCallback />
}

export default AuthCallback
